import React from 'react';

/**
 * Image optimization utilities for generating thumbnails and optimizing images
 */

export interface ImageOptimizationOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    format?: 'jpeg' | 'png' | 'webp';
}

/**
 * Creates a thumbnail from an image file
 */
export const createThumbnail = async (
    file: File,
    options: ImageOptimizationOptions = {}
): Promise<File> => {
    const {
        maxWidth = 300,
        maxHeight = 300,
        quality = 0.8,
        format = 'png'
    } = options;

    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate dimensions maintaining aspect ratio
            let { width, height } = img;

            if (width > height) {
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw the image
            ctx?.drawImage(img, 0, 0, width, height);

            // Convert to blob
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const thumbnailFile = new File([blob], `thumbnail_${file.name}`, {
                            type: `image/${format}`,
                        });
                        resolve(thumbnailFile);
                    } else {
                        reject(new Error('Failed to create thumbnail blob'));
                    }
                },
                `image/${format}`,
                quality
            );
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
    });
};

/**
 * Optimizes an image file for web display
 */
export const optimizeImage = async (
    file: File,
    options: ImageOptimizationOptions = {}
): Promise<File> => {
    const {
        maxWidth = 1200,
        maxHeight = 1200,
        quality = 0.9,
        format = 'png'
    } = options;

    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate dimensions maintaining aspect ratio
            let { width, height } = img;

            if (width > maxWidth || height > maxHeight) {
                const aspectRatio = width / height;

                if (width > height) {
                    width = Math.min(width, maxWidth);
                    height = width / aspectRatio;
                } else {
                    height = Math.min(height, maxHeight);
                    width = height * aspectRatio;
                }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw the image
            ctx?.drawImage(img, 0, 0, width, height);

            // Convert to blob
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const optimizedFile = new File([blob], file.name, {
                            type: `image/${format}`,
                        });
                        resolve(optimizedFile);
                    } else {
                        reject(new Error('Failed to create optimized image blob'));
                    }
                },
                `image/${format}`,
                quality
            );
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
    });
};

/**
 * Uploads both original and thumbnail versions to Supabase Storage
 */
export const uploadImageWithThumbnail = async (
    supabase: any,
    file: File,
    bucketName: string,
    folderPath: string = '',
    thumbnailOptions?: ImageOptimizationOptions
) => {
    try {
        // Create thumbnail
        const thumbnailFile = await createThumbnail(file, thumbnailOptions);

        // Optimize original image
        const optimizedFile = await optimizeImage(file);

        // Generate file names
        const fileExt = file.name.split('.').pop();
        const timestamp = Date.now();
        const fileName = `${timestamp}.${fileExt}`;
        const thumbnailFileName = `thumb_${timestamp}.${fileExt}`;

        const filePath = folderPath ? `${folderPath}/${fileName}` : fileName;
        const thumbnailPath = folderPath ? `${folderPath}/${thumbnailFileName}` : thumbnailFileName;

        // Upload original optimized image
        const { error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(filePath, optimizedFile);

        if (uploadError) throw uploadError;

        // Upload thumbnail
        const { error: thumbnailError } = await supabase.storage
            .from(bucketName)
            .upload(thumbnailPath, thumbnailFile);

        if (thumbnailError) throw thumbnailError;

        // Get public URLs
        const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);

        const { data: { publicUrl: thumbnailUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(thumbnailPath);

        return {
            imageUrl: publicUrl,
            thumbnailUrl: thumbnailUrl,
            fileName: fileName,
            thumbnailFileName: thumbnailFileName
        };
    } catch (error) {
        console.error('Error uploading image with thumbnail:', error);
        throw error;
    }
};

/**
 * Hook for lazy loading images with intersection observer
 */
export const useLazyImage = (src: string, thumbnailSrc?: string) => {
    const [imageSrc, setImageSrc] = React.useState(thumbnailSrc || '');
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [isInView, setIsInView] = React.useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
        }

        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (isInView && src) {
            const img = new Image();
            img.onload = () => {
                setImageSrc(src);
                setIsLoaded(true);
            };
            img.src = src;
        }
    }, [isInView, src]);

    return { imageSrc, isLoaded, imgRef };
};
